import { Component, h } from 'preact';
import moment from 'moment-timezone';

import getStore from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

import { InquiryDocumentType } from '../../../../../constants/inquiries';

import { inquiryService } from '../../../../../clientside/utils/inquiry.service';
import { ViewingListPropertyGroup } from 'inquiries/viewing-list.model';
import { InqDocumentModal, InquiryDocumentPropertySpecialOptions } from './inq-document-modal.component';
import { Listing } from '../../../../../types/listing.model';
import { InquiryDocument } from 'inquiries/inquiry-document.model';
import { ReqState } from '../../../../../types/request-state.model';
import { formatWithTimezone } from '../../../../core/date-formatter';
import { Inquiry } from '../../../../../types/inquiries';
import { InqDocumentCreateSuccessModal } from './inq-document-create-success-modal.component';

interface InqDocumentModalHostProps {
  documentType: InquiryDocumentType;
  inquiry: Inquiry;
  docItemToEdit?: InquiryDocument;

  onProceedToFileClick?: (doc: InquiryDocument) => void;
  onClose: () => void;
}

interface InqDocumentModalHostState {
  finalList?: ViewingListPropertyGroup;
  contractTemplates?: InquiryDocument[];

  templateKey?: string;
  document?: InquiryDocument;
  saveRequestState?: ReqState;
  hasPerformedFirstSave?: boolean;
  docSaved?: boolean;

  selectedProperty?: Listing;
  continuingWithoutProperty?: boolean;
}

// const TEST_INQUIRY_CODE = 'IQ29180'; // RENT
// const TEST_INQUIRY_CODE = 'IQ28938'; // SALE
const store = getStore();

export class InqDocumentModalHost extends Component<InqDocumentModalHostProps, InqDocumentModalHostState> {
  constructor(props: InqDocumentModalHostProps) {
    super(props);

    if (props.docItemToEdit) {
      this.state = {
        docSaved: !!(props.docItemToEdit.loiKey || props.docItemToEdit.contractKey)
      };
    }
  }

  async componentDidMount() {
    const inquiryCode = this.props.inquiry.inquiryCode; 
    const documentType = this.props.documentType;
    let docItem: InquiryDocument;

    if (documentType == InquiryDocumentType.OFFER) {
      try {
        let selectedProperty: Listing;
  
        const finalList = await inquiryService.getVLFinalList(inquiryCode);
        this.setState({ finalList });
  
        if (this.props.docItemToEdit) {
          docItem = await inquiryService.getOffer(inquiryCode, this.props.docItemToEdit.loiKey);
          docItem = this.ensureCwpDocumentDivCat(docItem);
  
          if (docItem.propertyKey) {
            selectedProperty = finalList.results.find(listing => listing.propertyKey === docItem.propertyKey);
          }
        }
  
        this.setState({ document: docItem, finalList, selectedProperty });
      } catch (err) {
        console.error(err);
      }
    } else if (documentType == InquiryDocumentType.CONTRACT) {
      try {
        const templates = await inquiryService.getContractTemplates(inquiryCode);

        if (this.props.docItemToEdit) {
          docItem = await inquiryService.getContract(inquiryCode, this.props.docItemToEdit.contractKey);
        }

        // The backend omits empty arrays -- add one if there is no "results" value.
        // The empty array lets us display a dialog with "Continue without a property"
        // as the only property option.
        const contractTemplates = templates.results || [];
        this.setState({
          contractTemplates,
          document: docItem
        });
      } catch (err) {
        console.error(err);
      }
    }
  }

  /**
   * Ensures that documents, especially those that were created through the
   * "Continue without a property" flow, contain the expected DivCat values.
   * 
   * @param document 
   */
  ensureCwpDocumentDivCat(document: InquiryDocument) {
    if (!document.division || !document.category || !document.type) {
      const { division, category, type } = this.props.inquiry;
      document = { ...document, division, category, type };
    }

    return document;
  }

  async onOfferTemplateSelectionChange(property: Listing) {
    const inquiry = this.props.inquiry;

    if (property) {
      const template = await inquiryService.getOfferTemplateByListing(inquiry.inquiryCode, property.propertyKey);

      this.setState({
        selectedProperty: property,
        document: template,
        continuingWithoutProperty: false,
      });
    } else {
      // Continue without a property
      // Pre-fill the document with the inquiry's DivCat data
      const template = await inquiryService.getEmptyOfferTemplate(inquiry.inquiryCode);
      this.setState({
        selectedProperty: null,
        continuingWithoutProperty: true,
        document: template
      });
    }
  }

  async onContractTemplateSelectionChange(key: string) {
    const inquiry = this.props.inquiry;

    if (key && key !== InquiryDocumentPropertySpecialOptions.CONTINUE_WITHOUT_PROPERTY) {
      try {
        const template = await inquiryService.getContractTemplateByKey(inquiry.inquiryCode, key);
        this.setState({ document: template, continuingWithoutProperty: false });
      } catch (err) {
        console.error(err);
      }
    } else if (key === InquiryDocumentPropertySpecialOptions.CONTINUE_WITHOUT_PROPERTY) {
      try {
        const template = await inquiryService.getEmptyContractTemplate(inquiry.inquiryCode);
        this.setState({ document: template, continuingWithoutProperty: true });
      } catch (err) {
        console.log(err);
      }
    } else {
      this.setState({ document: null });
    }

    this.setState({ templateKey: key });
  }

  onDocumentChange(document: InquiryDocument) {
    const oldDocument = this.state.document;

    if (document.leaseTerm !== undefined && document.leaseTerm <= 0) {
      document.leaseTerm = 1;
    }

    const leaseStartChanged = oldDocument.leaseStartDate != document.leaseStartDate;
    const leaseTermChanged = oldDocument.leaseTerm != document.leaseTerm;
    // Calculate lease end date
    if ((leaseStartChanged || leaseTermChanged) && document.leaseStartDate && document.leaseTerm >= 1) {
      if (document.leaseStartDate && document.leaseTerm) {
        document.leaseEndDate = formatWithTimezone(
          moment(document.leaseStartDate)
            .add(document.leaseTerm, 'months')
            .subtract(1, 'day')
        );
      }
    }

    // Calculate security deposit amount
    if (!isNaN(document.securityDeposit) && !isNaN(document.monthlyRent)) {
      document.securityDepositAmount = document.securityDeposit * document.monthlyRent;
    }

    // Calculate advance payment
    if (!isNaN(document.advancePayment) && !isNaN(document.monthlyRent)) {
      document.advancePaymentAmount = document.advancePayment * document.monthlyRent;
    }

    // Calculate commission
    if (!isNaN(document.commission) && !isNaN(document.monthlyRent)) {
      document.commissionAmount = document.commission * document.monthlyRent;
    }


    // Validate discount
    if (document.discountedPercent > 100) {
      document.discountedPercent = 100;
    } else if (document.discountedPercent < 0) {
      document.discountedPercent = 0;
    }
    
    // Validate downpayment
    if (document.downpayment > 100) {
      document.downpayment = 100;
    } else if (document.downpayment < 0) {
      document.downpayment = 0;
    }

    if (document.brokersFee > 100) {
      document.brokersFee = 100;
    } else if (document.brokersFee < 0) {
      document.brokersFee = 0;
    }

    // Calculate discounted price
    if (!isNaN(document.discountedPercent) && document.salePrice) {
      var discount = +((document.discountedPercent / 100) * document.salePrice).toFixed(2);
      document.discountedPrice = document.salePrice - discount;
    }
    
    // Calculate downpayment
    if (!isNaN(document.downpayment) && document.salePrice) {
      document.downpaymentAmount = +((document.downpayment / 100) * document.salePrice).toFixed(2);
    }

    if (oldDocument.balancePaidOffIn != document.balancePaidOffIn) {
      if (document.balancePaidOffIn) {
        document.balancePaidOffIn = Math.floor(document.balancePaidOffIn);
      }
    }

    this.setState({ document, docSaved: false });
  }

  async onOfferSave(document: InquiryDocument) {
    try {
      this.setState({ saveRequestState: ReqState.IN_PROGRESS });
      const newDocument = await inquiryService.saveOffer(this.props.inquiry.inquiryCode, document);
      this.setState({
        document: newDocument,
        saveRequestState: ReqState.SUCCESS,
        hasPerformedFirstSave: !document.loiKey
      });
      
      store.dispatch(InquiriesActions.attachmentUpdatedForInquiry(this.props.inquiry));
      // console.log('Offer save data:', newDocument);
    } catch (err) {
      this.setState({ saveRequestState: ReqState.FAILED });
      console.log('Failed to save offer:', err);
    }
  }

  async onContractSave(document: InquiryDocument) {
    try {
      this.setState({ saveRequestState: ReqState.IN_PROGRESS });
      const newDocument = await inquiryService.saveContract(this.props.inquiry.inquiryCode, document);
      
      this.setState({
        document: newDocument,
        saveRequestState: ReqState.SUCCESS,
        hasPerformedFirstSave: !document.contractKey
      });
      store.dispatch(InquiriesActions.attachmentUpdatedForInquiry(this.props.inquiry));
    } catch (err) {
      this.setState({ saveRequestState: ReqState.FAILED });
      console.log('Failed to save contract:', err);
    }
  }

  onSave(document: InquiryDocument) {
    if (this.props.documentType === InquiryDocumentType.OFFER) {
      return this.onOfferSave(document);
    } else {
      return this.onContractSave(document);
    }
  }

  async onPrint() {
    if (!this.state.docSaved) {
      await this.onSave(this.state.document);
      this.setState({ saveRequestState: ReqState.UNTOUCHED });
    }

    const link = inquiryService.getDocumentDownloadLink(this.props.inquiry, this.state.document);
    window.open(link, '_blank');
  }

  onTransitionCloseClick() {
    store.dispatch(InquiriesActions.loadAllStages());
    this.props.onClose();
  }

  render(props: InqDocumentModalHostProps, state: InqDocumentModalHostState) {
    if (props.documentType == InquiryDocumentType.OFFER && this.state.finalList && state.saveRequestState !== ReqState.SUCCESS) {
      return (
        <InqDocumentModal documentType={ InquiryDocumentType.OFFER }
          inquiry={ props.inquiry }
          propertyChoices={ state.finalList.results }
          selectedProperty={ state.selectedProperty }
          continuingWithoutProperty={ state.continuingWithoutProperty }
          document={ state.document }
          saveState={ state.saveRequestState }

          onDocumentChange={ document => this.onDocumentChange(document) }
          onOfferTemplateSelectionChange={ selectedProperty => this.onOfferTemplateSelectionChange(selectedProperty) }
          onSave={ document => this.onSave(document) }
          onPrint={ () => this.onPrint() }
          onClose={ props.onClose } />
      );
    } else if (props.documentType == InquiryDocumentType.CONTRACT && state.contractTemplates && state.saveRequestState !== ReqState.SUCCESS) {
      return (
        <InqDocumentModal documentType={ InquiryDocumentType.CONTRACT }
          inquiry={ props.inquiry }
          contractTemplates={ state.contractTemplates }
          document={ state.document }
          continuingWithoutProperty={ state.continuingWithoutProperty }
          saveState={ state.saveRequestState }
          selectedTemplateKey={ state.templateKey }

          onDocumentChange={ document => this.onDocumentChange(document) }
          onContractTemplateSelectionChange={ key => this.onContractTemplateSelectionChange(key) }
          onSave={ document => this.onSave(document) }
          onPrint={ () => this.onPrint() }
          onClose={ props.onClose }/>
      )
    } else if (state.saveRequestState === ReqState.SUCCESS) {
      return (
        <InqDocumentCreateSuccessModal onClose={ () => this.onTransitionCloseClick() }
          onProceedToLoiFileClick={ () => props.onProceedToFileClick(state.document) }
          showingForFirstSave={ state.hasPerformedFirstSave }
          documentType={ props.documentType } />
      )
    } else {
      return null;
    }
  }
}
